import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react'
import { Button, Card, CardContent, buttonVariants, cn } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'
import { NEXT_STEP_HREFS, OPEN_FORMAT_LINKS } from '../model/formats'
import {
  OpenImageViewerError,
  formatBytes,
  loadNativeImage,
  validateOpenImageFile,
} from '../model/openImageViewer'
import type {
  OpenImageViewerConfig,
  OpenImageViewerCopy,
  OpenImageViewerLoadedImage,
  OpenImageViewerStatus,
} from '../model/types'
import { ViewerExportButtons } from './ViewerExportButtons'

interface ViewerState {
  status: OpenImageViewerStatus
  file: File | null
  image: OpenImageViewerLoadedImage | null
  error?: string
}

interface OpenImageViewerShellProps {
  copy: OpenImageViewerCopy
  config: OpenImageViewerConfig
}

const EMPTY_VIEWER: ViewerState = {
  status: 'idle',
  file: null,
  image: null,
}

const ZOOM_STEP = 0.25
const MIN_ZOOM = 0.25
const MAX_ZOOM = 3
const BYTES_IN_MB = 1024 * 1024

function toToolErrorCode(code: string) {
  if (code === 'unsupported') return 'unsupported_input' as const
  if (code === 'too_large' || code === 'too_large_image') return 'size_limit' as const
  return 'processing_failed' as const
}

export function OpenImageViewerShell({ copy, config }: OpenImageViewerShellProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const revokeRef = useRef<(() => void) | null>(null)
  const loadAbortRef = useRef<AbortController | null>(null)
  const requestGenerationRef = useRef(0)
  const configRef = useRef(config)
  const copyRef = useRef(copy)
  configRef.current = config
  copyRef.current = copy
  const [viewer, setViewer] = useState<ViewerState>(EMPTY_VIEWER)
  const [dropActive, setDropActive] = useState(false)
  const [fitMode, setFitMode] = useState(true)
  const [zoom, setZoom] = useState(1)
  const isLoading = viewer.status === 'loading'

  const releasePreview = useCallback(() => {
    revokeRef.current?.()
    revokeRef.current = null
  }, [])

  useEffect(() => () => {
      requestGenerationRef.current += 1
      loadAbortRef.current?.abort()
      configRef.current.cancelPending?.()
      releasePreview()
    }, [releasePreview])

  const openFile = useCallback(
    async (file: File) => {
      const currentConfig = configRef.current
      const currentCopy = copyRef.current
      const requestGeneration = ++requestGenerationRef.current
      loadAbortRef.current?.abort()
      const loadAbortController = new AbortController()
      loadAbortRef.current = loadAbortController
      currentConfig.cancelPending?.()
      releasePreview()
      setFitMode(true)
      setZoom(1)

      const validationError = validateOpenImageFile(file, currentConfig, currentCopy)
      if (validationError) {
        const analyticsContext = currentConfig.analyticsContext ?? currentConfig.analytics
        const legacyAnalyticsKey = currentConfig.legacyAnalyticsKey ??
          (analyticsContext ? undefined : currentConfig.analyticsKey)
        if (analyticsContext) {
          trackToolRejection(analyticsContext, toToolErrorCode(validationError.code))
        } else {
          toolError(legacyAnalyticsKey ?? 'image-viewer', validationError.code)
        }
        setViewer({
          status: 'error',
          file,
          image: null,
          error: validationError.message,
        })
        return
      }

      const analyticsContext = currentConfig.analyticsContext ?? currentConfig.analytics
      const legacyAnalyticsKey = currentConfig.legacyAnalyticsKey ??
        (analyticsContext ? undefined : currentConfig.analyticsKey)
      const operation = analyticsContext
        ? startToolOperation(analyticsContext)
        : null
      if (!operation) {
        toolInvoke(legacyAnalyticsKey ?? 'image-viewer', {
          format: currentConfig.formatLabel,
          size: file.size,
        })
      }
      const startTime = performance.now()
      setViewer({ status: 'loading', file, image: null })

      try {
        if (currentConfig.validateFile) {
          const isValid = await currentConfig.validateFile(file, loadAbortController.signal)
          if (requestGeneration !== requestGenerationRef.current) return
          if (!isValid) {
            throw new OpenImageViewerError(currentCopy.errorsText.corrupt, 'corrupt')
          }
        }

        const image = currentConfig.loadImage
          ? await currentConfig.loadImage(file, loadAbortController.signal)
          : await loadNativeImage(file, currentConfig, currentCopy, loadAbortController.signal)
        if (requestGeneration !== requestGenerationRef.current) {
          image.revoke?.()
          return
        }
        revokeRef.current = image.revoke ?? null
        loadAbortRef.current = null
        setViewer({ status: 'ready', file, image })
        if (operation) {
          operation.success(performance.now() - startTime)
        } else {
          toolSuccess(legacyAnalyticsKey ?? 'image-viewer', Math.round(performance.now() - startTime), {
            format: image.formatLabel,
            width: image.width,
            height: image.height,
          })
        }
      } catch (error) {
        if (requestGeneration !== requestGenerationRef.current) return
        const mappedError = currentConfig.mapLoadError?.(error)
        const normalizedError = mappedError
          ? new OpenImageViewerError(mappedError.message, mappedError.code)
          : error
        loadAbortRef.current = null
        const code =
          normalizedError instanceof OpenImageViewerError
            ? normalizedError.code
            : 'corrupt'
        if (operation) {
          operation.error(toToolErrorCode(code))
        } else {
          toolError(legacyAnalyticsKey ?? 'image-viewer', code)
        }
        setViewer({
          status: 'error',
          file,
          image: null,
          error:
            normalizedError instanceof Error
              ? normalizedError.message
              : currentCopy.errorsText[code as keyof typeof currentCopy.errorsText] ??
                currentCopy.errorsText.corrupt,
        })
      }
    },
    [releasePreview],
  )

  const reset = useCallback(() => {
    requestGenerationRef.current += 1
    loadAbortRef.current?.abort()
    loadAbortRef.current = null
    configRef.current.cancelPending?.()
    releasePreview()
    setViewer(EMPTY_VIEWER)
    setDropActive(false)
    setFitMode(true)
    setZoom(1)
  }, [releasePreview])

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isLoading) return
      const file = event.target.files?.[0]
      if (file) {
        void openFile(file)
        event.target.value = ''
      }
    },
    [isLoading, openFile],
  )

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)

      if (isLoading) return
      const file = event.dataTransfer.files[0]
      if (file) {
        void openFile(file)
      }
    },
    [isLoading, openFile],
  )

  const facts = useMemo(() => {
    if (!viewer.file || !viewer.image) return []

    return [
      { label: t.viewer.fileName, value: viewer.file.name },
      { label: t.viewer.fileSize, value: formatBytes(viewer.file.size) },
      {
        label: copy.dimensions,
        value: viewer.image.dimensionsLabel ?? `${viewer.image.width} x ${viewer.image.height}`,
      },
      { label: t.viewer.mimeType, value: viewer.image.mimeLabel },
      { label: t.viewer.format, value: viewer.image.formatLabel },
      ...(viewer.image.facts ?? []),
    ]
  }, [copy.dimensions, t.viewer, viewer.file, viewer.image])

  const readyContext =
    viewer.status === 'ready' && viewer.file && viewer.image
      ? { file: viewer.file, image: viewer.image }
      : null
  const previewStyle =
    fitMode || !readyContext ? undefined : { width: readyContext.image.width * zoom }
  const stepZoom = (delta: number) => {
    setZoom((value) => {
      const next = (fitMode ? 1 : value) + delta
      return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next))
    })
    setFitMode(false)
  }
  const nextSteps = [
    { label: t.viewer.compress, href: NEXT_STEP_HREFS.compress },
    { label: t.viewer.crop, href: NEXT_STEP_HREFS.crop },
    { label: t.viewer.resize, href: NEXT_STEP_HREFS.resize },
    ...(config.convertHref ? [{ label: t.viewer.convert, href: config.convertHref }] : []),
  ]

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={[copy.acceptedFormats, `${Math.round(config.maxFileSizeBytes / BYTES_IN_MB)} MB`]}
        collapsed={readyContext !== null}
      />

      <input
        ref={inputRef}
        type="file"
        accept={config.accept}
        disabled={isLoading}
        className="hidden"
        onChange={handleInputChange}
      />

      {readyContext ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-6">
          <section
            className="-mx-4 min-w-0 border-y bg-card sm:mx-0 sm:rounded-lg sm:border sm:p-5"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 sm:mb-4 sm:px-0 sm:py-0">
              <div className="min-w-0 flex-1 basis-40">
                <h2 className="text-base font-semibold tracking-normal sm:text-lg">
                  {copy.previewTitle}
                </h2>
                <p className="truncate text-xs text-muted-foreground sm:text-sm">
                  {readyContext.file.name}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  type="button"
                  variant={fitMode ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFitMode(true)}
                >
                  {t.viewer.zoomFit}
                </Button>
                <Button
                  type="button"
                  variant={!fitMode && zoom === 1 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setFitMode(false)
                    setZoom(1)
                  }}
                >
                  100%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={t.viewer.zoomOut}
                  disabled={!fitMode && zoom <= MIN_ZOOM}
                  onClick={() => stepZoom(-ZOOM_STEP)}
                >
                  -
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={t.viewer.zoomIn}
                  disabled={!fitMode && zoom >= MAX_ZOOM}
                  onClick={() => stepZoom(ZOOM_STEP)}
                >
                  +
                </Button>
              </div>
            </div>

            <div
              className={cn(
                'max-h-[70vh] overflow-auto sm:rounded-md sm:border sm:p-3',
                config.previewBackground === 'checkerboard'
                  ? 'bg-background bg-[linear-gradient(45deg,hsl(var(--muted))_25%,transparent_25%),linear-gradient(-45deg,hsl(var(--muted))_25%,transparent_25%),linear-gradient(45deg,transparent_75%,hsl(var(--muted))_75%),linear-gradient(-45deg,transparent_75%,hsl(var(--muted))_75%)] bg-[length:24px_24px] bg-[position:0_0,0_12px,12px_-12px,-12px_0]'
                  : 'bg-muted',
              )}
            >
              <img
                src={readyContext.image.previewUrl}
                alt={copy.previewAlt}
                className={cn(
                  'mx-auto block',
                  fitMode ? 'max-h-[64vh] max-w-full object-contain' : 'max-w-none',
                )}
                style={previewStyle}
              />
            </div>

            {readyContext.image.notice && (
              <p className="m-4 rounded-md border bg-background px-3 py-2 text-sm leading-6 text-muted-foreground sm:mx-0 sm:mb-0">
                {readyContext.image.notice}
              </p>
            )}
          </section>

          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-3 p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">
                    {t.viewer.nextStepsTitle}
                  </h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>
                {config.renderActions?.(readyContext)}
                {!config.renderActions && !!config.exportFormats?.length && (
                  <ViewerExportButtons
                    context={readyContext}
                    config={config}
                    formats={config.exportFormats}
                  />
                )}
                <div className="grid grid-cols-2 gap-2">
                  {nextSteps.map((step) => (
                    <a
                      key={step.href}
                      href={step.href}
                      className={cn(
                        buttonVariants({ variant: 'outline' }),
                        'h-auto min-h-10 whitespace-normal px-2 text-center',
                      )}
                    >
                      {step.label}
                    </a>
                  ))}
                </div>
                {!config.acceptedByEditors && (
                  <p className="text-xs leading-5 text-muted-foreground">
                    {t.viewer.saveCopyFirst}
                  </p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    reset()
                    inputRef.current?.click()
                  }}
                >
                  {t.viewer.openAnother}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-4 sm:p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.viewer.factsTitle}</h2>
                <dl className="space-y-2 text-sm">
                  {facts.map((fact) => (
                    <div key={fact.label} className="grid gap-1">
                      <dt className="text-muted-foreground">{fact.label}</dt>
                      <dd className="break-words font-medium text-foreground">{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </aside>
        </div>
      ) : (
        <Card
          className={cn(
            'mx-auto max-w-3xl transition-colors',
            dropActive && 'border-primary bg-primary/5',
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CardContent className="p-0">
            <button
              type="button"
              disabled={isLoading}
              className="flex w-full flex-col items-center rounded-lg px-4 py-5 text-center outline-none focus-visible:ring-2 focus-visible:ring-ring sm:py-10"
              onClick={() => inputRef.current?.click()}
            >
              <span className="mb-5 hidden h-16 w-16 place-items-center rounded-md border bg-background text-xs font-semibold text-muted-foreground sm:grid">
                {config.formatLabel}
              </span>
              <span className="mb-4 hidden text-lg font-medium [@media(pointer:fine)]:block">
                {copy.dropTitle}
              </span>
              <span className={buttonVariants({ size: 'lg' })}>
                {isLoading ? copy.loading : copy.chooseFile}
              </span>
              <span className="mt-3 max-w-md text-sm leading-6 text-muted-foreground">
                {copy.dropDescription}
              </span>
            </button>
            {isLoading && (
              <div className="px-4 pb-5 text-center">
                <Button type="button" variant="outline" onClick={reset}>
                  {t.viewer.cancel}
                </Button>
              </div>
            )}
            {viewer.status === 'error' && (
              <p
                className="mx-auto max-w-md px-4 pb-5 text-center text-sm leading-6 text-destructive"
                role="alert"
              >
                {viewer.error}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <nav className="mx-auto mt-4 max-w-3xl" aria-label={t.viewer.otherFormatsTitle}>
        <h2 className="text-sm font-medium text-muted-foreground sm:text-center">
          {t.viewer.otherFormatsTitle}
        </h2>
        <ul className="mt-2 flex flex-wrap gap-2 sm:justify-center">
          {OPEN_FORMAT_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                aria-current={link.label === config.formatLabel ? 'page' : undefined}
                className={cn(
                  'block rounded-md border px-3 py-1.5 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                  link.label === config.formatLabel
                    ? 'border-foreground/20 bg-foreground text-background'
                    : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <p className="mx-auto mt-4 max-w-3xl text-xs leading-5 text-muted-foreground sm:text-center">
        {copy.privacyNote}
      </p>

      <section className="mt-10 border-t pt-8 sm:mt-12">
        <h2 className="text-xl font-semibold tracking-normal">{copy.howToTitle}</h2>
        <ol className="mt-4 grid gap-3 lg:grid-cols-3">
          {copy.howTo.map((step, index) => (
            <li
              key={step}
              className="rounded-md border bg-card p-4 text-sm leading-6 text-muted-foreground"
            >
              <span className="mr-2 font-semibold text-foreground">{index + 1}.</span>
              {step}
            </li>
          ))}
        </ol>
      </section>

      {!!config.relatedTools?.length && (
        <section className="mt-12 border-t pt-8">
          <h2 className="text-xl font-semibold tracking-normal">
            {copy.relatedToolsTitle}
          </h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {config.relatedTools.map((tool) => (
              <a
                key={tool.href}
                href={tool.href}
                className="rounded-md border bg-card p-4 transition-colors hover:border-primary hover:bg-primary/5"
              >
                <span className="font-medium text-foreground">{tool.title}</span>
                <span className="mt-2 block text-sm leading-6 text-muted-foreground">
                  {tool.description}
                </span>
              </a>
            ))}
          </div>
        </section>
      )}

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
